// src/stores/sessionStore.js
import { makeAutoObservable, reaction, runInAction } from 'mobx'
import { auth } from '../firebaseConfig' // один раз в самом верху
import {
	GoogleAuthProvider,
	onAuthStateChanged,
	signInWithEmailAndPassword,
	signInWithPopup,
	getIdToken,
} from 'firebase/auth'


import { config } from '../config'
import logger from '../utils/logger'

import apiClient from '../utils/apiClient'

class SessionStore {
	// Безопасный парсинг JSON с проверкой на undefined и null
	safeJsonParse(item) {
		try {
			return item ? JSON.parse(item) : null
		} catch (e) {
			logger.error(`Ошибка парсинга JSON: ${e.message}`)
			return null
		}
	}

	// Используем безопасный парсинг
	user = this.safeJsonParse(localStorage.getItem('user'))
	firebaseUser = null // объект, который возвращает сам Firebase (можно хранить отдельно)
	isAuthChecking = true
	userInfo = this.safeJsonParse(localStorage.getItem('userInfo'))
	authInitialized = false // Флаг инициализации Firebase Auth

	constructor() {
		makeAutoObservable(this)

		// Реакция на изменение user — сохраняем в localStorage
		reaction(
			() => this.user,
			user => {
				if (user) {
					localStorage.setItem('user', JSON.stringify(user))
				} else {
					localStorage.removeItem('user')
				}
			}
		)
		// Сохраняем userInfo в localStorage
		reaction(
			() => this.userInfo,
			userInfo => {
				if (userInfo) {
					localStorage.setItem('userInfo', JSON.stringify(userInfo))
				} else {
					localStorage.removeItem('userInfo')
				}
			}
		)

		// Настраиваем слушатель Firebase Auth State
		this.setupAuthStateListener()

		// Попытка восстановить сессию при инициализации
		this.restoreSession();
	}

	// Настройка слушателя состояния аутентификации Firebase
	setupAuthStateListener() {
		onAuthStateChanged(auth, (firebaseUser) => {
			runInAction(() => {
				this.firebaseUser = firebaseUser;
				this.authInitialized = true;
				this.isAuthChecking = false;
			});

			if (!firebaseUser) {
				// Пользователь вышел из системы
				logger.log('🚪 Firebase Auth: пользователь вышел из системы');
				runInAction(() => {
					this.user = null;
					this.userInfo = null;
				});
			} else {
				// Пользователь вошел в систему
				logger.log(`👤 Firebase Auth: пользователь ${firebaseUser.email} вошел в систему`);
			}
		});
	}

	// Ожидание инициализации Firebase Auth
	async waitForAuthInitialized() {
		if (this.authInitialized) return;
		
		return new Promise(resolve => {
			const checkInterval = setInterval(() => {
				if (this.authInitialized) {
					clearInterval(checkInterval);
					resolve();
				}
			}, 100); // проверяем каждые 100мс
			
			// Таймаут на случай, если что-то пойдет не так
			setTimeout(() => {
				clearInterval(checkInterval);
				resolve();
				logger.warn("Таймаут ожидания инициализации Firebase Auth");
			}, 5000);
		});
	}

	// Метод для восстановления сессии
	async restoreSession() {
		try {
			// Дожидаемся инициализации Firebase Auth
			await this.waitForAuthInitialized();
			
			const currentUser = auth.currentUser;
			if (currentUser) {
				logger.log(`Восстановление сессии для ${currentUser.email}`);
				
				try {
					// Получаем актуальный токен
					const token = await currentUser.getIdToken(true); // Принудительно обновляем
					
					// Проверяем токен на сервере и обновляем пользователя
					await this.fetchOrCreateUser(token);
					
					// Сессия успешно восстановлена
					if (this.user && this.user.id) {
						logger.log("Сессия успешно восстановлена");
						return true;
					}
				} catch (error) {
					logger.error("Ошибка получения токена при восстановлении сессии:", error);
				}
			} else {
				logger.log("Нет текущего пользователя Firebase для восстановления сессии");
			}
			return false;
		} catch (error) {
			logger.error('Ошибка восстановления сессии:', error);
			return false;
		}
	}

	// Метод проверки уникальности personalId
	async checkPersonalIdAvailability(personalId) {
		logger.log('🎈 ENDP-40 - checkPersonalIdAvailability')
		try {
			const response = await apiClient.get('/api/user/check-personalId', {
				params: { personalId }
			})
			const data = response.data
			// Ожидается, что сервер вернет объект вида: { available: true } если personalId свободен
			return data.available
		} catch (error) {
			logger.error('Ошибка в checkPersonalIdAvailability:', error)
			throw error
		}
	}

	// Метод для обновления информации о пользователе на сервере
	async updateUserInfo(updatedData) {
		logger.log('🎈 ENDP-41 - updateUserInfo')
		const userId = this.user.id // id пользователя
		// Объединяем userId с остальными данными
		const payload = { userId, ...updatedData }

		logger.log(`Новая информация о пользователе: ${JSON.stringify(payload)}`)

		try {
			const response = await apiClient.put('/api/user', payload)
			const data = response.data
			
			// Обновляем userInfo в сторе
			runInAction(() => {
				// Сохраняем текущие данные userInfo
				const currentUserInfo = this.userInfo || {};
				
				// Обновляем только полученные поля
				this.userInfo = {
					...currentUserInfo,
					name: data.name,
					personalId: data.personalId,
					agency: data.agency
				};
			});
			
			return data;
		} catch (error) {
			logger.error(error)
			throw error
		}
	}

	// запрашиваем персональные данные пользователя
/* 	async fetchUserInfo(userId) {
		logger.log('🎈 ENDP-42 - fetchUserInfo')
		logger.log(`Запрос данных пользователя: ${userId}`)
		try {
			const response = await apiClient.get(`/userinfo/${userId}`)
			const data = response.data

			runInAction(() => {
				// Сохраняем текущие данные, если они есть
				const currentUserInfo = this.userInfo || {};
				
				// Обновляем только полученные данные, сохраняя остальные
				this.userInfo = {
					...currentUserInfo,
					name: data.name,
					personalId: data.personalId,
					agency: data.agency,
					contacts: data.contacts || [] // Добавляем контакты
				}
			})
			
			return data;
		} catch (error) {
			logger.error('Ошибка загрузки userInfo:', error)
			throw error;
		}
	} */

	// Метод для добавления контакта пользователя
	async addUserContact(contactData) {
		logger.log('🎈 ENDP-43 - addUserContact')
		
		try {
			const response = await apiClient.post('/api/user/contact', contactData)
			const newContact = response.data
			
			// Обновляем контакты в сторе
			runInAction(() => {
				// Сохраняем все текущие поля userInfo
				const updatedUserInfo = { ...this.userInfo };
				
				// Проверяем и инициализируем массив контактов если нужно
				if (!updatedUserInfo.contacts) {
					updatedUserInfo.contacts = [];
				}
				
				// Добавляем новый контакт
				updatedUserInfo.contacts.push(newContact);
				
				// Обновляем userInfo целиком
				this.userInfo = updatedUserInfo;
			})
			
			return newContact
		} catch (error) {
			logger.error('Ошибка при добавлении контакта:', error)
			throw error
		}
	}

	// Метод для редактирования контакта пользователя
	async updateUserContact(contactId, contactData) {
		logger.log('🎈 ENDP-45 - updateUserContact')
		
		try {
			const response = await apiClient.put(`/api/user/contact/${contactId}`, contactData)
			const updatedContact = response.data
			
			// Обновляем контакт в сторе
			runInAction(() => {
				// Сохраняем все текущие поля userInfo
				const updatedUserInfo = { ...this.userInfo };
				
				if (updatedUserInfo.contacts) {
					const contactIndex = updatedUserInfo.contacts.findIndex(c => c.id === contactId)
					if (contactIndex !== -1) {
						updatedUserInfo.contacts[contactIndex] = updatedContact;
					}
					
					// Обновляем userInfo целиком
					this.userInfo = updatedUserInfo;
				}
			})
			
			return updatedContact
		} catch (error) {
			logger.error('Ошибка при редактировании контакта:', error)
			throw error
		}
	}

	// Метод для удаления контакта пользователя
	async deleteUserContact(contactId) {
		logger.log('🎈 ENDP-46 - deleteUserContact')
		
		try {
			const response = await apiClient.delete(`/api/user/contact/${contactId}`)
			
			// Удаляем контакт из стора
			runInAction(() => {
				// Сохраняем все текущие поля userInfo
				const updatedUserInfo = { ...this.userInfo };
				
				if (updatedUserInfo.contacts) {
					updatedUserInfo.contacts = updatedUserInfo.contacts.filter(c => c.id !== contactId);
					
					// Обновляем userInfo целиком
					this.userInfo = updatedUserInfo;
				}
			})
			
			return response.data
		} catch (error) {
			logger.error('Ошибка при удалении контакта:', error)
			throw error
		}
	}

	// Загрузка контактов пользователя
	async fetchUserContacts() {
		logger.log('🎈 ENDP-44 - fetchUserContacts')
		
		try {
			const response = await apiClient.get('/api/user/contacts')
			const contacts = response.data
			
			// Обновляем контакты в сторе
			runInAction(() => {
				// Сохраняем все текущие поля userInfo
				const updatedUserInfo = this.userInfo ? { ...this.userInfo } : {};
				
				// Обновляем только поле contacts
				updatedUserInfo.contacts = contacts;
				
				// Обновляем userInfo целиком
				this.userInfo = updatedUserInfo;
			})
			
			return contacts
		} catch (error) {
			logger.error('Ошибка при загрузке контактов:', error)
			throw error
		}
	}

	async fetchOrCreateUser(token) {
		try {
			const url = `${config.apiUrl}/auth/verify`
			
			const resp = await fetch(url, {
				method: 'POST',
				headers: { 'Content-Type': 'application/json' },
				body: JSON.stringify({ token }),
			})
			
			if (!resp.ok) {
				throw new Error(`HTTP error! status: ${resp.status}`)
			}
			
			const data = await resp.json()
			runInAction(() => {
				this.user = data
			})
			
			logger.log('Пользователь успешно создан/получен:', data)
			
		} catch (err) {
			logger.error('Ошибка при запросе /auth/verify:', err)
			throw err
		}
	}

	// Регистрация по email и паролю (Firebase)
	async registerByEmail(email, password) {
		const { createUserWithEmailAndPassword, sendEmailVerification } =
			await import('firebase/auth')
		const userCredential = await createUserWithEmailAndPassword(
			auth,
			email,
			password
		)

		logger.log('Пользователь создан: ', userCredential.user)

		// Отправляем письмо
		await sendEmailVerification(userCredential.user, {
			url: 'https://.../login', // куда вернёт после клика на «Подтвердить»
		})

		logger.log('Письмо для подтверждения выслано')
	}

	// Авторизация по email и паролю
	async loginByEmail(email, password) {
		logger.log('🎈 ENDP-44 - loginByEmail')
		try {
			const userCredential = await signInWithEmailAndPassword(
				auth,
				email,
				password
			)
			// Store email in localStorage
			localStorage.setItem('userEmail', userCredential.user.email)
			logger.log('Успешная авторизация по email')

			// Получаем токен Firebase и создаем/получаем пользователя
			const token = await userCredential.user.getIdToken()
			await this.fetchOrCreateUser(token)
		} catch (error) {
			logger.error('Ошибка при авторизации по email:', error)
			throw error
		}
	}

	// Авторизация по google
	async loginByGoogle() {
		logger.log('🎈 ENDP-45 - loginByGoogle')
		try {
			const provider = new GoogleAuthProvider()
			provider.setCustomParameters({
				prompt: 'select_account',
			})
			const result = await signInWithPopup(auth, provider)
			// Store email in localStorage
			localStorage.setItem('userEmail', result.user.email)
			
			logger.log(`🎎 result.user.email: ${result.user.email}`)

			// Получаем токен Firebase и создаем/получаем пользователя
			const token = await result.user.getIdToken()
			await this.fetchOrCreateUser(token)
	
			logger.log('Успешная авторизация через Google')
		} catch (error) {
			logger.error('Ошибка при авторизации через Google:', error)
			throw error
		}
	}

	// Выход
	async logout() {
		try {
			logger.log('🚪 Начинаем процесс logout в sessionStore');
			
			const { signOut } = await import('firebase/auth')
			await signOut(auth)
			
			// Очищаем все данные из localStorage
			localStorage.removeItem('userEmail')
			localStorage.removeItem('user')
			localStorage.removeItem('userInfo')
			
			// Сбрасываем состояние в MobX
			runInAction(() => {
				this.user = null
				this.userInfo = null
				this.firebaseUser = null
				this.isAuthChecking = false
			})
			
			// Сбрасываем состояние shopStore
			try {
				const shopStore = await import('./shopStore')
				shopStore.default.reset()
				logger.log('🏪 Состояние shopStore сброшено');
			} catch (error) {
				logger.error('Ошибка при сбросе shopStore:', error);
			}

			// Сбрасываем состояние partnershipStore
			try {
				const partnershipStore = await import('./partnershipStore')
				partnershipStore.default.reset()
				logger.log('🤝 Состояние partnershipStore сброшено');
			} catch (error) {
				logger.error('Ошибка при сбросе partnershipStore:', error);
			}
			
			logger.log('🚪 Logout в sessionStore завершен успешно');
		} catch (error) {
			logger.error('Ошибка при logout в sessionStore:', error);
			throw error;
		}
	}
}

const sessionStore = new SessionStore()
export default sessionStore
